/**
 * IR → Mermaid text emitter (reverse conversion path).
 * Produces valid mermaid flowchart syntax from an IRGraph.
 */

import type { IRGraph, IRNode } from '../types';
import { NodeShape } from '../types';

export function renderMermaid(graph: IRGraph): string {
  const lines: string[] = [];
  const dir = graph.direction === 'TD' || graph.direction === 'TB' ? 'TD' : graph.direction;
  lines.push(`flowchart ${dir}`);

  // Group nodes by lane
  const laneNodes = new Map<string, string[]>();
  const orphanNodes: string[] = [];

  for (const [id, node] of graph.nodes) {
    if (node.laneId && node.laneId !== '_general') {
      if (!laneNodes.has(node.laneId)) laneNodes.set(node.laneId, []);
      laneNodes.get(node.laneId)!.push(id);
    } else {
      orphanNodes.push(id);
    }
  }

  // Render lanes as subgraphs
  for (const lane of graph.lanes) {
    if (lane.id === '_general') continue;
    const nodeIds = laneNodes.get(lane.id) ?? [];
    if (nodeIds.length === 0) continue;

    lines.push(`  subgraph ${lane.id}[${lane.title}]`);
    for (const nid of nodeIds) {
      const node = graph.nodes.get(nid);
      if (node) {
        lines.push(`    ${formatNode(node)}`);
      }
    }
    lines.push('  end');
    lines.push('');
  }

  // Render orphan nodes
  for (const nid of orphanNodes) {
    const node = graph.nodes.get(nid);
    if (node) {
      lines.push(`  ${formatNode(node)}`);
    }
  }

  // Render edges
  lines.push('');
  for (const edge of graph.edges) {
    let line = `  ${edge.source}`;
    if (edge.dashed) {
      line += edge.label ? ` -. ${edge.label} .-> ` : ' -.-> ';
    } else {
      line += edge.label ? ` -- ${edge.label} --> ` : ' --> ';
    }
    line += edge.target;
    lines.push(line);
  }

  return lines.join('\n');
}

function formatNode(node: IRNode): string {
  const id = node.id;
  const label = node.label;

  switch (node.shape) {
    case NodeShape.StartCircle:
      return `${id}((${label || 'Start'}))`;
    case NodeShape.EndCircle:
      return `${id}(((${label || 'End'})))`;
    case NodeShape.Decision:
      return `${id}{${label}}`;
    case NodeShape.Datastore:
      return `${id}[(${label})]`;
    case NodeShape.Subroutine:
      return `${id}[[${label}]]`;
    case NodeShape.Process:
    default:
      return `${id}[${label}]`;
  }
}
